import urlParse from 'url-parse';
import queryString from 'qs';

import toISODateString from './toISODateString';
import { formatFullAddress } from './formatAddress';

function urlJoin(...segments) {
  return segments.reduce((left, right) => `${left.replace(/\/$/, '')}/${right.replace(/^\//, '')}`);
}

function removeWidgetPath(pathname) {
  return pathname.replace(/\/(widget\/?)?\s*$/, '');
}

function addWidgetPath(pathname) {
  return urlJoin(removeWidgetPath(pathname), '/widget/');
}

function filterParams(params) {
  return Object.keys(params).reduce((memo, key) => {
    const value = params[key];
    if (value !== undefined) {
      memo[key] = value == null ? '' : value;
    }
    return memo;
  }, {});
}

function toPathString(url) {
  let { query } = url;
  const { pathname } = url;
  if (typeof query === 'string') {
    query = query.replace(/^\?/, '');
  } else {
    query = queryString.stringify(url.query);
  }
  if (query === '') {
    return pathname;
  }
  return `${pathname}?${query}`;
}

export function getQueryFromLocation(location) {
  return location ? queryString.parse(location.search.replace(/^\?/, '')) : {};
}

export function updateParams(history, params, { replace = false } = {}) {
  const updater = replace ? history.replace : history.push;
  const updatedQuery = getQueryFromLocation(history.location);
  const query = { ...updatedQuery, ...filterParams(params) };
  updater.call(history, {
    pathname: history.location.pathname,
    query,
    search: queryString.stringify(query),
  });
}

export function widgetizeUrl(url) {
  const parsedUrl = urlParse(url);
  parsedUrl.set('pathname', addWidgetPath(parsedUrl.pathname));
  return toPathString(parsedUrl);
}

function addParamsToUrl(url, params) {
  const parsedUrl = urlParse(url);
  const currentParams = queryString.parse(parsedUrl.query.replace(/^\?/, ''));
  parsedUrl.set('query', filterParams({ ...currentParams, ...params }));
  return toPathString(parsedUrl);
}

export function addFlashMessageToUrl(url, message) {
  return addParamsToUrl(url, { message });
}

export function buildProviderSchedulingUrl(
  providerId,
  { location = null, params = {}, widget = false } = {}
) {
  const parsedUrl = urlParse(
    widget ? `/providers/${providerId}/widget/` : `/providers/${providerId}/`
  );
  const query = getQueryFromLocation(location);
  if (query) {
    params = {
      insuranceIssuerId: query.insurance_issuer_id || query.insuranceIssuerId,
      patientStatusId: query.patient_status_id || query.patientStatusId,
      reasonForVisitId: query.reason_for_visit_id || query.reasonForVisitId,
      minDate: toISODateString(query.minDate) || undefined,
      after: query.after,
      ...params,
    };
  }
  parsedUrl.set('query', filterParams(params));
  return toPathString(parsedUrl);
}

export function buildOfficeSchedulingUrl(officeId, { location, params = {} } = {}) {
  const parsedUrl = urlParse(`/offices/${officeId}/widget/`);
  const query = getQueryFromLocation(location);
  if (query) {
    params = {
      insuranceIssuerId: query.insuranceIssuerId,
      specialtyId: query.specialtyId,
      patientStatusId: query.patientStatusId,
      reasonForVisitId: query.reasonForVisitId,
      minDate: query.minDate,
      after: query.after,
      ...params,
    };
  }
  parsedUrl.set('query', filterParams(params));
  return toPathString(parsedUrl);
}

export function buildPracticeSchedulingUrl(practiceId, { location } = {}) {
  const parsedUrl = urlParse(`/practices/${practiceId}/widget/`);
  const query = getQueryFromLocation(location);

  if (query) {
    parsedUrl.set(
      'query',
      filterParams({
        insuranceIssuerId: query.insuranceIssuerId,
        specialtyId: query.specialtyId,
        patientStatusId: query.patientStatusId,
        reasonForVisitId: query.reasonForVisitId,
        minDate: query.minDate,
        after: query.after,
      })
    );
  }
  return toPathString(parsedUrl);
}

export function buildOpeningUrl(
  providerId,
  openingId,
  { location, params = {}, widget = false } = {}
) {
  const match = openingId.match(/(\d{8}T\d{6}Z)[_-](\w{7})$/);
  if (!match) {
    throw new Error(`Could not parse opening id '${openingId}'`);
  }
  const openingSlug = `${match[1]}-${match[2]}`;
  const parsedUrl = urlParse(
    widget
      ? `/providers/${providerId}/openings/${openingSlug}/widget/`
      : `/providers/${providerId}/openings/${openingSlug}/`
  );
  const query = getQueryFromLocation(location);

  if (query) {
    params = {
      insurance_issuer_id: query.insurance_issuer_id || query.insuranceIssuerId,
      patient_status_id: query.patient_status_id || query.patientStatusId,
      reason_for_visit_id: query.reason_for_visit_id || query.reasonForVisitId,
      minDate: toISODateString(query.minDate) || undefined,
      ...params,
    };
  }
  parsedUrl.set('query', filterParams(params));
  return toPathString(parsedUrl);
}

export function buildAppointmentUrl(
  providerId,
  appointmentId,
  { patientToken, params = {}, useMainLayout = false } = {}
) {
  const parsedUrl = urlParse(
    useMainLayout
      ? `/providers/${providerId}/appointments/${appointmentId}/main/`
      : `/providers/${providerId}/appointments/${appointmentId}/`
  );
  parsedUrl.set(
    'query',
    filterParams({
      token: patientToken,
      ...params,
    })
  );
  return toPathString(parsedUrl);
}

export function buildRescheduleUrl(
  appointmentId,
  { useMainLayout = false, patientToken, params = {} } = {}
) {
  const parsedUrl = urlParse(
    useMainLayout
      ? `/appointments/${appointmentId}/reschedule/main/`
      : `/appointments/${appointmentId}/reschedule/`
  );

  parsedUrl.set(`query`, filterParams({ token: patientToken, ...params }));

  return toPathString(parsedUrl);
}

export function buildCancelAppointmentUrl(
  appointmentId,
  { useMainLayout = false, patientToken, params } = {}
) {
  const parsedUrl = urlParse(
    useMainLayout
      ? `/appointments/${appointmentId}/cancel/main/`
      : `/appointments/${appointmentId}/cancel/`
  );
  parsedUrl.set(`query`, filterParams({ token: patientToken, ...params }));
  return toPathString(parsedUrl);
}

export function buildApptCalendarUrl(
  providerId,
  appointmentId,
  { patientToken, params = {} } = {}
) {
  const parsedUrl = urlParse(`/providers/${providerId}/appointments/${appointmentId}.ics`);

  parsedUrl.set(`query`, filterParams({ token: patientToken, ...params }));

  return toPathString(parsedUrl);
}

export function buildOfficeDirectionsUrl(address, { params } = {}) {
  const parsedUrl = urlParse(`http://www.google.com/maps`);
  const fullAddress = formatFullAddress(address);
  parsedUrl.set(`query`, filterParams({ daddr: fullAddress, ...params }));
  return parsedUrl.toString();
}

export function buildLoginUrl({ reason, next, params } = {}) {
  const parsedUrl = urlParse('/auth/login/patient/');
  parsedUrl.set('query', filterParams({ reason, next, ...params }));
  return toPathString(parsedUrl);
}

export function buildAppointmentCanceledUrl({ isSignedIn } = {}) {
  const parsedUrl = urlParse(isSignedIn ? '/profile/' : '/');
  parsedUrl.set('query', { message: 'canceled' });
  return toPathString(parsedUrl);
}

export function buildLocationUrl({ countrySlug, stateSlug, citySlug }, { location } = {}) {
  let parsedUrl;
  const query = getQueryFromLocation(location);

  if (citySlug && stateSlug) {
    parsedUrl = urlParse(`/locations/${countrySlug}/${stateSlug}/${citySlug}/`);
  } else if (stateSlug) {
    parsedUrl = urlParse(`/locations/${countrySlug}/${stateSlug}/`);
  } else {
    parsedUrl = urlParse(`/locations/${countrySlug}/`);
  }

  if (query) {
    parsedUrl.set(
      'query',
      filterParams({
        insuranceIssuerId: query.insuranceIssuerId,
        specialtyId: query.specialtyId,
        patientStatusId: query.patientStatusId,
        reasonForVisitId: query.reasonForVisitId,
        minDate: query.minDate,
      })
    );
  }

  return toPathString(parsedUrl);
}
